import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['all', 'Bebés', 'Bodas', 'Primera Comunión', 'Embarazo', 'Publicidad', 'Familia'];

export const Portfolio = () => {
  const [galleries, setGalleries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch(`${API}/galleries`);
        const data = await response.json();
        // Sort by date descending (most recent first)
        const sorted = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setGalleries(sorted);
      } catch (error) {
        console.error('Error fetching galleries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGalleries();
  }, []);

  // Get limited galleries: max 2 per category when not showing all
  const displayedGalleries = useMemo(() => {
    if (activeCategory !== 'all') {
      // When filtering by category, show all of that category or limit based on showAll
      const filtered = galleries.filter(g => g.category === activeCategory);
      return showAll ? filtered : filtered.slice(0, 6);
    }
    
    if (showAll) {
      return galleries;
    }
    
    // Show max 2 per category, maintaining chronological order
    const categoryCount = {};
    return galleries.filter(gallery => {
      const cat = gallery.category;
      categoryCount[cat] = (categoryCount[cat] || 0) + 1;
      return categoryCount[cat] <= 2;
    });
  }, [galleries, activeCategory, showAll]);

  // Check if there are more galleries to show
  const hasMore = useMemo(() => {
    if (activeCategory !== 'all') {
      const filtered = galleries.filter(g => g.category === activeCategory);
      return filtered.length > displayedGalleries.length;
    }
    return galleries.length > displayedGalleries.length;
  }, [galleries, activeCategory, displayedGalleries]);

  // Reset showAll when changing category
  useEffect(() => {
    setShowAll(false);
  }, [activeCategory]);

  return (
    <section id="portfolio" className="py-20 md:py-32 bg-muted" data-testid="portfolio-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Portfolio
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
            Mis Trabajos
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Una selección de mis proyectos más destacados. Cada galería cuenta una historia única.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                activeCategory === category
                  ? 'bg-foreground text-background'
                  : 'bg-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`filter-${category.toLowerCase()}`}
            >
              {category === 'all' ? 'Todos' : category}
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-[4/3] bg-card animate-pulse" />
            ))}
          </div>
        ) : (
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence>
              {displayedGalleries.map((gallery, index) => (
                <motion.div
                  key={gallery.gallery_id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link 
                    to={`/gallery/${gallery.gallery_id}`}
                    className="group block"
                    data-testid={`gallery-${gallery.gallery_id}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-card">
                      <img
                        src={gallery.thumbnail}
                        alt={gallery.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white text-xs uppercase tracking-widest">Ver galería</span>
                      </div>
                    </div>
                    <div className="pt-4">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {gallery.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {gallery.date}
                        </span>
                      </div>
                      <h3 className="font-serif text-xl mb-1">{gallery.title}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{gallery.category}</span>
                        <span className="flex items-center gap-1 text-xs text-amber-500">
                          <Star className="w-3 h-3 fill-current" />
                          {gallery.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Show More/Less Button */}
        {(hasMore || showAll) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              variant="outline" 
              onClick={() => setShowAll(!showAll)}
              className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-xs"
              data-testid="portfolio-toggle"
            >
              {showAll ? (
                <>
                  Ver menos
                  <ChevronUp className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Ver más galerías
                  <ChevronDown className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};
